import { useState } from 'react';
import { useUser, useClerk } from '@clerk/clerk-react';
import { resetAnalytics } from '@tagadapay/analytics';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { trpc } from '@/lib/api/trpc';
import { Loader2 } from 'lucide-react';

export default function CompleteProfilePage() {
  const { user, isLoaded: isUserLoaded } = useUser();
  const { signOut } = useClerk();
  const navigate = useNavigate();

  const [email, setEmail] = useState(() => user?.primaryEmailAddress?.emailAddress ?? '');
  const [firstName, setFirstName] = useState(() => user?.firstName ?? '');
  const [lastName, setLastName] = useState(() => user?.lastName ?? '');
  const [error, setError] = useState<string | null>(null);

  const { data: userCheck, isLoading: isChecking } = trpc.users.checkUserExists.useQuery(
    undefined,
    { enabled: isUserLoaded },
  );

  const createProfile = trpc.users.createUserProfile.useMutation({
    onSuccess: () => {
      navigate('/no-organization', { replace: true });
    },
    onError: (err) => {
      setError(err.message);
    },
  });

  // Still loading
  if (!isUserLoaded || isChecking) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Loader2 className="text-muted-foreground h-8 w-8 animate-spin" />
      </div>
    );
  }

  // User already exists in DB — redirect to normal flow
  if (userCheck?.exists) {
    navigate('/no-organization', { replace: true });
    return null;
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    createProfile.mutate({ email, firstName, lastName });
  };

  return (
    <div className="bg-background flex h-screen items-center justify-center p-4">
      <div className="w-full max-w-md space-y-4">
        <Card className="p-6">
          <CardContent className="space-y-6 p-0">
            <div className="space-y-1">
              <h1 className="text-2xl font-semibold tracking-tight">Complete your profile</h1>
              <p className="text-muted-foreground text-sm">
                We need a few details to set up your account.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="firstName">First name</Label>
                <Input
                  id="firstName"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="lastName">Last name</Label>
                <Input
                  id="lastName"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  required
                />
              </div>

              {error && (
                <p className="text-destructive text-sm">{error}</p>
              )}

              <Button
                type="submit"
                className="w-full"
                disabled={createProfile.isPending}
              >
                {createProfile.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating...
                  </>
                ) : (
                  'Continue'
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        <div className="text-center">
          <Button
            variant="link"
            onClick={() => {
              resetAnalytics();
              signOut();
            }}
            size="sm"
            className="text-muted-foreground"
          >
            Sign out and try a different account
          </Button>
        </div>
      </div>
    </div>
  );
}
